import { useState } from 'react';
import {
  Controller,
  UseFormHandleSubmit,
  UseFormSetError,
  Control,
  FieldErrors,
  UseFormSetValue,
} from 'react-hook-form';
import {
  Filters,
  InputNumber,
  InputText,
  ResponseError,
  responseErrorsResolver,
  Select,
  Sort,
} from '@ui/src';
import { useAppSelector } from '@store';
import { RiskScoreItem } from '../types';
import { RiskScoreRange } from '../../store/dictionaries/types';
import getRiskScore from './get-risk-score';

import cn from './risk-score-form.module.css';

export interface UseRiskScoreProps {
  onDeleteRiskScore?: ReturnType<any>[0];
  getListAction?: ({
    filters,
    sort,
  }: {
    filters?: Filters;
    sort?: Sort;
  }) => Promise<void>;
  getList?: (query: string) => void;
  selectedList?: string[];
  filters?: Filters;
  sort?: Sort;
  handleSubmit?: UseFormHandleSubmit<any>;
  itemId?: string;
  setError?: UseFormSetError<any>;
  update?: ReturnType<any>[0];
  create?: ReturnType<any>[0];
  withClientType?: boolean;
  control?: Control<any>;
  errors?: FieldErrors<any>;
  setValue?: UseFormSetValue<any>;
  nameTitle?: string;
  disabled?: boolean;
}

let timeout: NodeJS.Timeout;

const useRiskScore = ({
  onDeleteRiskScore,
  getListAction,
  selectedList,
  filters,
  sort,
  handleSubmit,
  itemId,
  setError,
  getList,
  update,
  create,
  withClientType,
  control,
  setValue,
  errors,
  nameTitle,
  disabled,
}: UseRiskScoreProps) => {
  const { riskScoreRanges } = useAppSelector((state) => state.amlDictionaries);
  const [globalError, setGlobalError] = useState('');

  const onDelete = async () => {
    const response =
      onDeleteRiskScore &&
      ((await onDeleteRiskScore({ ids: selectedList })) as {
        error: ResponseError;
      });

    if (!response?.error && getListAction) {
      getListAction({ filters, sort });
    }
  };

  const onFormSubmit =
    handleSubmit &&
    handleSubmit(async (values) => {
      setGlobalError('');

      const body: {
        name: string;
        riskScoreRangeId: string;
        riskScore: number;
        clientType?: string;
      } = {
        name: values.name,
        riskScoreRangeId: values.riskScoreRangeId,
        riskScore: Number(values.riskScore),
      };

      if (withClientType) {
        body.clientType = values.clientType;
      }

      let response;

      if (itemId) {
        response =
          update &&
          ((await update({
            id: itemId,
            body,
          })) as {
            data?: RiskScoreItem;
            error: ResponseError;
          });
      } else {
        response =
          create &&
          ((await create(body)) as {
            data: RiskScoreItem;
            error: ResponseError;
          });
      }

      if (response?.error && setError) {
        responseErrorsResolver({
          errors: response.error,
          setError,
          setGlobalError,
        });
      } else if (!response?.error && !itemId && getList) {
        getList('?take=30');
      }
    });

  const form = (
    <div className={cn.riskScoreForm}>
      <div className={cn.riskScoreFormRow}>
        <Controller
          control={control}
          name="name"
          defaultValue=""
          render={({ field: { onChange, value, name } }) => (
            <InputText
              name={name}
              placeholder={`Enter ${
                nameTitle?.toLocaleLowerCase() || 'source'
              } name`}
              onChange={onChange}
              value={value}
              title={nameTitle || 'Source'}
              error={errors?.name?.message as string}
              className={cn.riskScoreInput}
              labelClassName={cn.riskScoreInputLabel}
              disabled={disabled}
              tabIndex={disabled ? -1 : 0}
            />
          )}
        />
      </div>
      <div className={cn.riskScoreFormRow}>
        <Controller
          control={control}
          name="riskScore"
          defaultValue=""
          render={({ field: { onChange, value, name } }) => (
            <InputNumber
              name={name}
              placeholder="Enter risk score"
              onChange={(e) => {
                clearTimeout(timeout);

                onChange(e.target.value);

                timeout = setTimeout(() => {
                  const currentRange = getRiskScore({
                    value: e.target.value,
                    riskScoreRanges: riskScoreRanges as RiskScoreRange[],
                  });

                  if (setValue) {
                    setValue('riskScoreRangeId', currentRange as string);
                  }
                }, 300);
              }}
              value={value}
              title="Risk score"
              error={errors?.riskScore?.message as string}
              disabled={disabled}
              tabIndex={disabled ? -1 : 0}
            />
          )}
        />
      </div>
      <div className={cn.sourceOfFundsFormRow}>
        <Controller
          control={control}
          name="riskScoreRangeId"
          defaultValue=""
          render={({ field: { onChange, value, name } }) => (
            <>
              <Select
                list={riskScoreRanges}
                placeholder="Choose risk score range"
                name={name}
                title="Risk Score Range"
                value={value}
                onChange={(e) => {
                  onChange(e.target.value);

                  const currentRange = riskScoreRanges?.find(
                    (range) => range.value === e.target.value,
                  );

                  if (currentRange && setValue) {
                    setValue('riskScore', currentRange.from.toString());
                  }
                }}
                selectWrapperClassName={`${cn.riskScoreSelect} ${
                  errors?.riskScoreRangeId?.message
                    ? cn.riskScoreSelectError
                    : ''
                }`}
                disabled={disabled}
                tabIndex={disabled ? -1 : 0}
                fullWidth
              />
              {errors?.riskScoreRangeId?.message && (
                <div className={cn.error}>
                  {errors?.riskScoreRangeId?.message as string}
                </div>
              )}
            </>
          )}
        />
        {withClientType && (
          <Controller
            control={control}
            name="clientType"
            defaultValue=""
            render={({ field: { value, name } }) => (
              <input type="hidden" name={name} value={value} />
            )}
          />
        )}
      </div>
    </div>
  );

  return {
    onDelete,
    onFormSubmit,
    form,
    globalError,
  };
};

export default useRiskScore;
